
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CreditCard, Wallet, Shield, HomeIcon } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface InvestmentDialogProps {
  property: Tables<"real_estate_properties">;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  isProcessing: boolean;
  onInvest: (type: 'full' | 'loan' | 'collateral', amount: string) => Promise<void>;
}

export const InvestmentDialog = ({ 
  property, 
  showDialog, 
  setShowDialog, 
  isProcessing, 
  onInvest 
}: InvestmentDialogProps) => {
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [activeTab, setActiveTab] = useState("buy");

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Purchase Property</DialogTitle>
          <DialogDescription>
            Choose your preferred way to acquire this property
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="buy" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="buy">Buy Outright</TabsTrigger>
            <TabsTrigger value="finance">Finance</TabsTrigger>
            <TabsTrigger value="collateral">Collateral</TabsTrigger>
          </TabsList>
          
          <TabsContent value="buy" className="space-y-4 pt-4">
            <div className="space-y-2">
              <h3 className="font-medium">Full Purchase</h3>
              <p className="text-sm text-muted-foreground">
                Buy the property outright and add it to your portfolio
              </p>
              <div className="bg-slate-50 p-3 rounded border border-slate-100">
                <div className="flex justify-between text-sm mb-1">
                  <span>Property price:</span>
                  <span className="font-medium">GHS {property.price.toLocaleString()}</span>
                </div>
                {property.expected_returns && (
                  <div className="flex justify-between text-sm">
                    <span>Expected annual returns:</span>
                    <span className="font-medium text-green-600">{property.expected_returns}%</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={() => onInvest('full', property.price.toString())}
              disabled={isProcessing}
              className="w-full"
              variant="default"
            >
              <HomeIcon className="mr-2 h-4 w-4" />
              {isProcessing ? "Processing..." : "Buy Now"}
            </Button>
          </TabsContent>
          
          <TabsContent value="finance" className="space-y-4 pt-4">
            <div className="space-y-2">
              <h3 className="font-medium">Financing Options</h3>
              <p className="text-sm text-muted-foreground">
                Finance your purchase with our flexible payment plans
              </p>
              <label className="text-sm font-medium">Down Payment Amount (GHS)</label>
              <Input
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                placeholder="Enter amount"
                min={property.price * 0.2}
                max={property.price * 0.8}
              />
              <p className="text-xs text-muted-foreground">
                Minimum down payment: GHS {(property.price * 0.2).toLocaleString()}
              </p>
            </div>
            <Button
              onClick={() => onInvest('loan', investmentAmount || (property.price * 0.3).toString())}
              disabled={isProcessing}
              className="w-full"
              variant="default"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Apply for Financing
            </Button>
          </TabsContent>
          
          <TabsContent value="collateral" className="space-y-4 pt-4">
            <div className="space-y-2">
              <h3 className="font-medium">Use as Collateral</h3>
              <p className="text-sm text-muted-foreground">
                Buy the property and immediately use it as collateral for loans
              </p>
              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                <div className="flex justify-between text-sm mb-1">
                  <span>Purchase price:</span>
                  <span className="font-medium">GHS {property.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Available as collateral:</span>
                  <span className="font-medium text-purple-600">GHS {property.collateral_value?.toLocaleString() || (property.price * 0.7).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <Button
              onClick={() => onInvest('collateral', property.price.toString())}
              disabled={isProcessing}
              className="w-full"
              variant="default"
            >
              <Shield className="mr-2 h-4 w-4" />
              Buy & Use as Collateral
            </Button>
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => setShowDialog(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
